import { useMemo } from 'react';
import { useListIntegrations } from '@plerionhq/design-system';

import { useIntegrationGroups } from './use-integration-groups';
import { useScopedOmniFilter } from './use-scoped-omni-filter';

export type IntegrationGroupFilterOption = {
  groupId: string;
  name: string;
};

export type IntegrationGroupFilterDefaults = {
  type: 'integrationGroup';
  id: string;
  label: string;
  data: IntegrationGroupFilterOption[];
  error: unknown;
  loading: boolean;
};

type UseIntegrationGroupFilterOptions = {
  isEnabled?: boolean;
  isNarrowedByOmniFilter?: boolean;
  selectedGroupIds?: string[];
};

const DEFAULT_FILTER_ID = 'integrationGroupIds';
const DEFAULT_FILTER_LABEL = 'Integration group';

const readSelectedGroupIdsFromUrl = (): string[] =>
  new URLSearchParams(window.location.search).getAll(DEFAULT_FILTER_ID);

export const useIntegrationGroupFilter = ({
  isEnabled = true,
  isNarrowedByOmniFilter = false,
  selectedGroupIds,
}: UseIntegrationGroupFilterOptions = {}): {
  filter: IntegrationGroupFilterDefaults;
  groups: IntegrationGroupFilterOption[];
  isVisible: boolean;
} => {
  const isQueryEnabled = isEnabled;

  const {
    data: groupsData,
    error,
    isLoading: isLoadingGroups,
  } = useIntegrationGroups({ enabled: isQueryEnabled });

  const { data: integrationsData, isLoading: isLoadingIntegrations } =
    useListIntegrations({ enabled: isQueryEnabled });

  const omniFilterState = useScopedOmniFilter();
  const omniSelectedGroupIds = omniFilterState.integrationGroupIds;

  const hasSelection =
    (selectedGroupIds ?? readSelectedGroupIdsFromUrl()).length > 0 ||
    (omniSelectedGroupIds || []).length > 0;

  return useMemo(() => {
    const selectableGroups = (groupsData || []).filter(
      ({ status }) => status !== 'orphaned',
    );

    const isNarrowed =
      isNarrowedByOmniFilter && (omniSelectedGroupIds || []).length > 0;

    const groups: IntegrationGroupFilterOption[] = selectableGroups
      .filter(
        ({ groupId }) => !isNarrowed || omniSelectedGroupIds?.includes(groupId),
      )
      .map(({ groupId, name }) => ({
        groupId,
        name,
      }));

    const isSettled = !isLoadingGroups && !isLoadingIntegrations;
    const accessibleIntegrationCount = (integrationsData || []).length;
    const onlyGroupMemberCount =
      selectableGroups.length === 1
        ? (selectableGroups[0].memberIntegrationIds || []).length
        : undefined;

    // Group visibility and the integrations list are both narrowed server-side
    // to the caller's Integration:Read reach, so members are always a subset of
    // that list and an equal count means the group covers all of it.
    const isCoveredBySingleGroup =
      onlyGroupMemberCount !== undefined &&
      accessibleIntegrationCount > 0 &&
      onlyGroupMemberCount === accessibleIntegrationCount;

    const isProvablyUseless =
      isSettled &&
      !hasSelection &&
      !error &&
      (selectableGroups.length === 0 || isCoveredBySingleGroup);

    return {
      filter: {
        type: 'integrationGroup',
        id: DEFAULT_FILTER_ID,
        label: DEFAULT_FILTER_LABEL,
        data: groups,
        error,
        loading: isLoadingGroups || isLoadingIntegrations,
      },
      groups,
      isVisible: isQueryEnabled && !isProvablyUseless,
    };
  }, [
    error,
    groupsData,
    hasSelection,
    integrationsData,
    isLoadingGroups,
    isLoadingIntegrations,
    isNarrowedByOmniFilter,
    isQueryEnabled,
    omniSelectedGroupIds,
  ]);
};
